import React from 'react'
import { Link } from 'react-router-dom'
import { theme } from 'antd'
import { PiArrowUpRight } from 'react-icons/pi'
import { radius, shadows, spacing } from '@vacationtracker/shared/constants/ThemeSetting'

const CARD = 'vt-record-card'
const LINK = 'vt-record-card-link'
const TOP = 'vt-record-card-top'
const STYLE_ID = 'vt-record-card-styles'

// Hover, focus-within and the whole-card click target cannot be expressed as inline styles, so
// this one stylesheet is injected once. Colours come through CSS custom properties so the rules
// stay static while the palette stays token-driven.
const recordCardStyles = `
.${CARD} {
  position: relative;
  border: 1px solid var(--vt-record-card-border);
  border-radius: ${radius.lg}px;
}
.${CARD}:has(.${LINK}) {
  cursor: pointer;
  transition: border-color 120ms ease-out, box-shadow 120ms ease-out;
}
.${CARD}:has(.${LINK}):hover,
.${CARD}:has(.${LINK}):focus-within {
  border-color: var(--vt-record-card-hover-border);
  box-shadow: ${shadows.base};
}
.${CARD} .${LINK}::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
}
.${CARD} .${TOP} { position: relative; z-index: 1; }
@media (prefers-reduced-motion: reduce) {
  .${CARD}:has(.${LINK}) { transition: none; }
}
`

const VISUALLY_HIDDEN: React.CSSProperties = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }

const BUTTON_RESET: React.CSSProperties = { background: 'none', border: 0, padding: 0, font: 'inherit', textAlign: 'left', cursor: 'pointer' }

// Injected by the card itself rather than by the grid, so a card outside a grid still gets its
// border, hover and click target.
function useRecordCardStyles() {
  React.useEffect(() => {
    if (document.getElementById(STYLE_ID)) return
    const style = document.createElement('style')
    style.id = STYLE_ID
    style.textContent = recordCardStyles
    document.head.appendChild(style)
  }, [])
}

export interface RecordCardShellProps {
  /** Resting height. Content that needs more room grows the card rather than being clipped. */
  height?: number | 'auto'
  /** A visible resting border and the brand colour on hover, for a list where each card is a place to go. */
  prominent?: boolean
  children: React.ReactNode
}

/**
 * The card's border, hover and click target without an arrangement, for cards that lay out their own content.
 * The card becomes a click target when a RecordCardLink is inside it.
 */
export function RecordCardShell({ height = 'auto', prominent, children }: RecordCardShellProps) {
  const { token } = theme.useToken()
  useRecordCardStyles()

  return (
    <div
      className={CARD}
      style={
        {
          '--vt-record-card-border': prominent ? token.colorBorder : token.colorBorderSecondary,
          '--vt-record-card-hover-border': prominent ? token.colorPrimary : token.colorTextTertiary,
          // minHeight, not height: a card whose facts wrap onto more rows grows rather than
          // clipping them. alignContent then centres the content in whatever height is left over.
          minHeight: height === 'auto' ? undefined : height,
          padding: `${spacing.sm}px ${spacing.base}px`,
          background: token.colorBgContainer,
          overflow: 'hidden',
          display: 'grid',
          alignContent: 'center',
        } as React.CSSProperties
      }
    >
      {children}
    </div>
  )
}

interface RecordCardRouterLinkProps {
  to: string
  onClick?: () => void
  children: React.ReactNode
}

interface RecordCardExternalLinkProps {
  href: string
  external: true
  /** Read to screen readers after the title, so the DS stays free of copy. */
  newTabLabel: string
  onClick?: () => void
  children: React.ReactNode
}

interface RecordCardButtonLinkProps {
  onClick: () => void
  children: React.ReactNode
}

export type RecordCardLinkProps = RecordCardRouterLinkProps | RecordCardExternalLinkProps | RecordCardButtonLinkProps

/** The card's link: an app route, a page in a new tab, or an action. Its ::after overlay makes the entire card clickable, so use it once per card. */
export function RecordCardLink(props: RecordCardLinkProps) {
  const { token } = theme.useToken()
  const style: React.CSSProperties = { color: token.colorText, minWidth: 0 }

  if ('to' in props) {
    return (
      <Link to={props.to} onClick={props.onClick} className={LINK} style={style}>
        {props.children}
      </Link>
    )
  }
  if ('href' in props) {
    return (
      <a href={props.href} target="_blank" rel="noopener noreferrer" onClick={props.onClick} className={LINK} style={style}>
        {props.children}
        <PiArrowUpRight aria-hidden style={{ marginLeft: spacing.xxs, verticalAlign: '-0.1em', color: token.colorTextTertiary }} />
        <span style={VISUALLY_HIDDEN}> ({props.newTabLabel})</span>
      </a>
    )
  }
  return (
    <button type="button" onClick={props.onClick} className={LINK} style={{ ...style, ...BUTTON_RESET }}>
      {props.children}
    </button>
  )
}

/** Wraps a control inside a linked card so it sits above the link overlay and keeps its own click. */
export function RecordCardAction({ children }: { children: React.ReactNode }) {
  return <span className={TOP}>{children}</span>
}

export interface RecordCardProps {
  /** Makes the whole card a click target. Omit for a read-only card. */
  to?: string
  /** Resting height. Content that needs more room grows the card rather than being clipped. */
  height?: number | 'auto'
  title: React.ReactNode
  /** Badges shown beside the title. */
  flags?: React.ReactNode
  /** Secondary detail line, usually RecordCardFacts. */
  facts?: React.ReactNode
  /** Trailing content, right-aligned. */
  aside?: React.ReactNode
  /** See RecordCardShell. */
  prominent?: boolean
}

export function RecordCard({ to, height = 'auto', title, flags, facts, aside, prominent }: RecordCardProps) {
  return (
    <RecordCardShell height={height} prominent={prominent}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'center',
          gap: spacing.sm,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xxs, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, flexWrap: 'wrap', minWidth: 0 }}>
            {to ? <RecordCardLink to={to}>{title}</RecordCardLink> : title}
            {flags}
          </div>
          {facts}
        </div>
        {aside}
      </div>
    </RecordCardShell>
  )
}
